import axios from 'axios'
import {FC, useEffect, useMemo, useState} from 'react'
import {useQuery, useQueryClient} from 'react-query'
import {API_URL} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth/core/Auth'
import {AuthModel} from '../../auth/core/_models'
import {TermsStatus} from '../core/_models'
import {
  acceptTerms,
  getCurrentTerms,
  getTermsDocumentQueryKey,
  getTermsStatus,
  getTermsStatusQueryKey,
  isTermsVersionChanged,
  readTermsError,
  shouldRetryTermsRead,
} from '../core/_requests'
import {shouldCheckTermsAcceptance} from '../core/termsAccess'
import {TermsAcceptanceModal} from './TermsAcceptanceModal'

// The answer only changes when a new version is published or someone else in the business
// accepts, so a fresh read on entering the panel, on switching business and now and then on
// returning to the tab is enough.
const STATUS_STALE_MS = 30 * 60 * 1000

/**
 * Mounted once in the panel layout: asks the server whether the selected business, or since
 * version 2 the user personally, still has to accept the terms in force, and if so keeps the
 * acceptance window open over the panel. A failed read never locks anyone out; the window simply
 * does not open.
 */
export const TermsAcceptanceGate: FC = () => {
  const {currentUser, saveAuth, setCurrentUser, logout} = useAuth()
  const queryClient = useQueryClient()
  const enabled = shouldCheckTermsAcceptance(currentUser)
  const userId = Number(currentUser?.id || 0)
  const companyId = Number(currentUser?.cpi || 0)
  const statusKey = getTermsStatusQueryKey(userId, companyId)

  const status = useQuery(statusKey, ({signal}) => getTermsStatus(signal), {
    enabled,
    staleTime: STATUS_STALE_MS,
    refetchOnWindowFocus: true,
    retry: shouldRetryTermsRead,
  })
  const businessDue = Boolean(
    enabled &&
      status.data?.available &&
      status.data.mustAccept &&
      Number(status.data.companyId) === companyId
  )
  // Version 2 on: each user accepts personally, once for all their businesses.
  const personalDue = Boolean(enabled && status.data?.available && status.data.userMustAccept)
  const mustAccept = businessDue || personalDue
  const document = useQuery(getTermsDocumentQueryKey(), ({signal}) => getCurrentTerms(signal), {
    enabled: mustAccept,
    staleTime: STATUS_STALE_MS,
    retry: shouldRetryTermsRead,
  })

  const [submitting, setSubmitting] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState<string>()

  useEffect(() => setError(undefined), [userId, companyId])

  const otherCompanies = useMemo(
    () =>
      (currentUser?.cpl || [])
        .filter((company) => Number(company.id) > 0 && Number(company.id) !== companyId)
        .map((company) => ({id: Number(company.id), name: company.nm || `کسب‌وکار ${company.id}`})),
    [currentUser?.cpl, companyId]
  )

  if (!mustAccept || !document.data) return null
  const terms = document.data

  const accept = async () => {
    if (submitting || switching) return
    setSubmitting(true)
    setError(undefined)
    try {
      const result = await acceptTerms({
        version: terms.version,
        contentHash: terms.contentHash,
        channel: 'prompt',
      })
      // With no business selected only the personal acceptance is recorded: there is no business record to add.
      const acceptance = result?.acceptance
      queryClient.setQueryData<TermsStatus | undefined>(statusKey, (previous) =>
        previous
          ? {
              ...previous,
              mustAccept: false,
              reason: null,
              userMustAccept: false,
              userAcceptedVersion: terms.version,
              acceptances: acceptance
                ? [
                    acceptance,
                    ...previous.acceptances.filter((item) => item.version !== acceptance.version),
                  ]
                : previous.acceptances,
            }
          : previous
      )
      if (!acceptance) queryClient.invalidateQueries(statusKey)
    } catch (acceptError) {
      if (isTermsVersionChanged(acceptError)) {
        setError(
          'متن قوانین و مقررات همین حالا به‌روزرسانی شد. نسخه تازه را تا انتها بخوانید و دوباره بپذیرید.'
        )
        await Promise.all([
          queryClient.invalidateQueries(getTermsDocumentQueryKey()),
          queryClient.invalidateQueries(statusKey),
        ])
      } else {
        setError(
          readTermsError(
            acceptError,
            'ثبت پذیرش انجام نشد. اتصال اینترنت را بررسی کنید و دوباره «خواندم و می‌پذیرم» را بزنید.'
          )
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Same as the business menu: the session moves to the other business and the panel reloads.
  const switchCompany = async (nextCompanyId: number) => {
    if (submitting || switching) return
    setSwitching(true)
    setError(undefined)
    try {
      const response = await axios.post<AuthModel>(`${API_URL}/auth/changeCompany`, {
        compnyId: nextCompanyId,
      })
      saveAuth(response.data)
      setCurrentUser(response.data)
      window.location.reload()
    } catch (switchError) {
      setError(readTermsError(switchError, 'ورود به کسب‌وکار دیگر انجام نشد. دوباره تلاش کنید.'))
      setSwitching(false)
    }
  }

  return (
    <TermsAcceptanceModal
      document={terms}
      reason={status.data?.reason}
      personalOnly={personalDue && !businessDue}
      companyName={
        status.data?.companyName ||
        currentUser?.cpl?.find((company) => Number(company.id) === companyId)?.nm
      }
      // Another business does not lift a personal acceptance.
      otherCompanies={businessDue ? otherCompanies : []}
      submitting={submitting}
      switching={switching}
      error={error}
      onAccept={accept}
      onLogout={() => logout()}
      onSwitchCompany={switchCompany}
    />
  )
}
